// hooks/useChat.js

import { useState, useRef, useEffect } from 'react';
import { scrollToBottom, generateId } from '../utils/chatHelpers';
import { savePDFToCache } from '../utils/pdf_cache_setup';
import apiService from '../services/apiService';

// API Configuration
const WEB_SERVER_API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export function useChat() {
  // states for loading right pane API calls
  const [isLoadingSuggestions, setIsLoadingSuggestions] = useState(false);
  const [isLoadingReport, setIsLoadingReport] = useState(false);


  // State
  const [chatStarted, setChatStarted] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [turns, setTurns] = useState([]);
  const [activeSuggestionTurnId, setActiveSuggestionTurnId] = useState(null);
  const [suggestions, setSuggestions] = useState({});
  const [typingTurnId, setTypingTurnId] = useState(null);
  const [typingSuggestionId, setTypingSuggestionId] = useState(null);

  // Track valid messages and files for API calls
  const [validUserMessages, setValidUserMessages] = useState([]);
  const [allFiles, setAllFiles] = useState([]);
  const [allFilesMetadata, setAllFilesMetadata] = useState([]); // Metadata for display/Firestore

  // Extract optimizations and reports state
  const [extractedSuggestions, setExtractedSuggestions] = useState({}); // turnId -> {key: description}
  const [selectedSuggestions, setSelectedSuggestions] = useState({}); // turnId -> [checked keys]
  const [reports, setReports] = useState({}); // turnId -> [{version: 1, content: '...', measurement: '...'}]
  const [activeReportId, setActiveReportId] = useState(null); // {turnId, version}
  const [typingReportId, setTypingReportId] = useState(null); // {turnId, version}
  const [reportLoadingPhase, setReportLoadingPhase] = useState('init');  // 'measurements', 'costs', 'streaming', or 'init'


  // Refs
  const prevActiveTurnRef = useRef(null);
  const prevActiveReportRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const rightPaneRef = useRef(null);
  const isNearBottomRef = useRef(true);
  const isRightNearBottomRef = useRef(true);
  const userScrolledAwayRef = useRef(false);
  const userScrolledAwayRightRef = useRef(false);

  // ✅ Session tracking
  const [sessionId, setSessionId] = useState(null);
  const [chatName, setChatName] = useState('Untitled Chat');

  // ✅ Track turn mappings (client turn ID -> Firestore turn ID)
  const turnMappingsRef = useRef({}); // { clientTurnId: firestoreTurnId }

  // Auto-scroll effect for left pane
  useEffect(() => {
    if (typingTurnId !== null && !userScrolledAwayRef.current) {
      scrollMessagesToBottom();
    }
  }, [turns, typingTurnId]);

  // Auto-scroll effect for right pane (suggestions)
  useEffect(() => {
    if (typingSuggestionId !== null && !userScrolledAwayRightRef.current) {
      scrollRightToBottom();
    }
  }, [suggestions, typingSuggestionId]);

  // Auto-scroll effect for right pane (reports)
  useEffect(() => {
    if (typingReportId !== null && !userScrolledAwayRightRef.current) {
      scrollRightToBottom();
    }
  }, [reports, typingReportId]);


  useEffect(() => {
    const currentTurnId = activeSuggestionTurnId;
    const currentReportId = activeReportId;

    // Determine if user navigated away from suggestions view
    const wasViewingSuggestions = prevActiveTurnRef.current !== null && prevActiveReportRef.current === null;
    const isNowViewingReport = currentTurnId !== null && currentReportId !== null;
    const isNowViewingDifferentTurn = prevActiveTurnRef.current !== null &&
      prevActiveTurnRef.current !== currentTurnId;
    const isNowClosingPane = prevActiveTurnRef.current !== null && currentTurnId === null;

    // Clear selections when:
    // 1. Switching from suggestions to report (same turn)
    // 2. Switching to different turn's suggestions
    // 3. Closing the right pane
    if (wasViewingSuggestions && isNowViewingReport && prevActiveTurnRef.current === currentTurnId) {
      // Case 1: Same turn, but switched from suggestions to report
      const turnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => {
        const updated = { ...prev };
        delete updated[turnId];
        return updated;
      });
    } else if (isNowViewingDifferentTurn) {
      // Case 2: Switched to different turn
      const previousTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => {
        const updated = { ...prev };
        delete updated[previousTurnId];
        return updated;
      });
    } else if (isNowClosingPane) {
      // Case 3: Closed right pane
      const previousTurnId = prevActiveTurnRef.current;
      setSelectedSuggestions(prev => {
        const updated = { ...prev };
        delete updated[previousTurnId];
        return updated;
      });
    }

    // Update refs
    prevActiveTurnRef.current = currentTurnId;
    prevActiveReportRef.current = currentReportId;
  }, [activeSuggestionTurnId, activeReportId]);

  useEffect(() => {
    return () => {
      if (sessionId) {
        apiService.closeSession(sessionId).catch(err =>
          console.error('Error closing session:', err)
        );
      }
    };
  }, [sessionId]);

  // Scroll handlers
  const onScrollMessages = () => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const threshold = 80;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    isNearBottomRef.current = distance < threshold;
    if (typingTurnId !== null && distance > threshold) {
      userScrolledAwayRef.current = true;
    }
  };

  const handleRightPaneScroll = () => {
    const el = rightPaneRef.current;
    if (!el) return;
    const threshold = 80;
    const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    isRightNearBottomRef.current = distanceFromBottom < threshold;
    if (typingSuggestionId !== null && distanceFromBottom > threshold) {
      userScrolledAwayRightRef.current = true;
    }
  };

  const handleUpdateChatName = async (newName) => {
    if (!sessionId) return;

    try {
      await apiService.updateChatName(sessionId, newName);
      setChatName(newName);
      console.log('✅ Chat name updated:', newName);
    } catch (error) {
      console.error('Error updating chat name:', error);
    }
  };

  // ✅ File upload handler
  const handleFileUpload = async (file, turnNumber, sessionIdTemp) => {
    console.log('inside handleFileUpload', sessionId, sessionIdTemp);
    if (!sessionId && !sessionIdTemp) {
      console.warn('Session not initialized, skipping file upload');
      return null;
    }

    try {
      console.log(`🚀 Uploading file: ${file.name}`);
      const result = await apiService.uploadFile(sessionId || sessionIdTemp, turnNumber, file);

      if (result.status === 'success' && result.file) {
        console.log(`✅ File uploaded:`, result.file);
        return result.file;
      }

      return null;
    } catch (error) {
      console.error('File upload error:', error);
      return null;
    }
  };

  // ✅ Save assistant message feedback handler (UPDATED VERSION)
  const saveFeedbackManually = async (turnId, feedbackType, comment = '') => {
    if (!sessionId) return;

    const firestoreTurnId = turnMappingsRef.current[turnId];
    if (!firestoreTurnId) {
      console.warn('No Firestore turn ID found for client turn:', turnId);
      return;
    }

    try {
      if (feedbackType === null) {
        // ✅ Remove feedback
        await apiService.removeFeedback(sessionId, firestoreTurnId);
        console.log('✅ Assistant message feedback removed');
      } else {
        // Save or update feedback
        await apiService.saveFeedback(sessionId, firestoreTurnId, {
          type: feedbackType,
          comment
        });
        console.log('✅ Assistant message feedback saved');
      }
    } catch (error) {
      console.error('Error saving assistant message feedback:', error);
    }
  };



  const scrollMessagesToBottom = () => scrollToBottom(messagesContainerRef);
  const scrollRightToBottom = () => scrollToBottom(rightPaneRef);

  // API Call: Guardrail Check
  const checkGuardrail = async (message, files) => {
  try {
    const response = await fetch(`${WEB_SERVER_API_BASE_URL}/api/analysis/guardrail`, {
      
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        message,
        files: files ? await convertFilesToBase64(files) : [],
      }),
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Guardrail API Error (via web server):', error);
    throw error;
  }
};

  // Convert files to base64 for API
  const convertFilesToBase64 = async (files) => {
    const filePromises = files.map(file => {
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64String = reader.result.split(',')[1];
          resolve({
            name: file.name,
            type: file.type,
            content: base64String
          });
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
    });
    return Promise.all(filePromises);
  };

  // API Call: Get Clarifications (Streaming)
  const streamClarifications = async (userMessages, fileMsgs, onChunk) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/get_clarifications_stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_messages: userMessages,
          file_msgs: fileMsgs,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
      if (!userScrolledAwayRef.current) {
        setTimeout(scrollMessagesToBottom, 0);
      }
    }
  } catch (error) {
    console.error('Clarifications API Error:', error);
    throw error;
  }
};


  // API Call: Get Optimizations (Streaming)
  const streamOptimizations = async (userMessages, fileMsgs, onChunk) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/get_optimizations_stream`,
      
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_messages: userMessages,
          file_msgs: fileMsgs,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
      if (!userScrolledAwayRightRef.current) {
        setTimeout(scrollRightToBottom, 0);
      }
    }
  } catch (error) {
    console.error('Optimizations API Error:', error);
    throw error;
  }
};


  // API Call: Extract Optimizations
  const extractOptimizations = async (assessmentText) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/extract_optimizations`,
      
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ assessment_text: assessmentText }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Extract Optimizations API Error:', error);
    throw error;
  }
};


  // API Call: Get Measurements
  const getMeasurements = async (userMessages, fileMsgs) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/get_measurements`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_messages: userMessages,
          file_msgs: fileMsgs,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Measurements API Error:', error);
    throw error;
  }
};

  const getMaterialCostList = async (
  userMessages,
  optimizationText,
  acceptedSuggestions,
  measurements,
) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/material_cost_list`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_messages: userMessages,
          assessment_text: optimizationText,
          accepted_suggestions: acceptedSuggestions,
          measurements: measurements,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('Get Material Cost List API Error:', error);
    throw error;
  }
};


  // API Call: Generate Report (Streaming)
  const streamReport = async (
  userMessages,
  assessmentText,
  acceptedSuggestions,
  measurements,
  costDetails,
  onChunk
) => {
  try {
    const response = await fetch(
      `${WEB_SERVER_API_BASE_URL}/api/analysis/generate_report_stream`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          user_messages: userMessages,
          assessment_text: assessmentText,
          accepted_suggestions: acceptedSuggestions,
          measurements: measurements,
          cost_details: costDetails,
        }),
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      const chunk = decoder.decode(value, { stream: true });
      onChunk(chunk);
      if (!userScrolledAwayRightRef.current) {
        setTimeout(scrollRightToBottom, 0);
      }
    }
  } catch (error) {
    console.error('Report Generation API Error:', error);
    throw error;
  }
};


  // Simulate streaming text (for error messages only)
  const streamText = async (text, onChunk, speed = 15) => {
    for (const ch of text) {
      onChunk(ch);
      if (!userScrolledAwayRef.current) {
        setTimeout(scrollMessagesToBottom, 0);
      }
      await new Promise((resolve) => setTimeout(resolve, speed));
    }
  };

  const handleSend = async (text, files) => {
    setChatStarted(true); //to split the sccreen

    setIsSending(true); // Set send btn loading at the very start
    let clientTurnId = null; // Local variable to track this turn's ID for the entire function scope
    try {
      let currentSessionId = sessionId;
      if (!currentSessionId) {
        try {
          console.log('🔄 Initializing session...');
          // Generate formatted timestamp in UAE timezone: YYYY-MM-DD HH:mm:ss
          const now = new Date();
          const uaeDate = new Date(now.toLocaleString('en-US', { timeZone: 'Asia/Dubai' }));

          const pad = (num) => String(num).padStart(2, '0');
          const sessionName = `${uaeDate.getFullYear()}-${pad(uaeDate.getMonth() + 1)}-${pad(uaeDate.getDate())} ${pad(uaeDate.getHours())}:${pad(uaeDate.getMinutes())}:${pad(uaeDate.getSeconds())}`;
          const result = await apiService.createSession(sessionName, 'india');
          currentSessionId = result.sessionId;
          setSessionId(currentSessionId);
          console.log('✅ Session initialized:', currentSessionId, result);
        } catch (error) {
          console.error('❌ Failed to initialize session:', error);
          // Continue without Firestore - don't block the chat
        }
      }

      clientTurnId = generateId();
      // Get current turn number
      const currentTurnNumber = turns.length + 1;

      // Create file metadata immediately from File objects
      // This gives you the display info (name, type, size) without waiting for upload
      const immediateFileData = files && files.length > 0
        ? files.map(file => ({
          name: file.name,
          type: file.type,
          size: file.size,
          uploading: true, // ← Flag to show loading state in UI
          // These will be added after upload completes:
          downloadURL: null,
          storagePath: null,
        }))
        : [];

      // Display user message IMMEDIATELY with empty files
      const newTurn = {
        id: clientTurnId,
        userText: text || '',
        botFollowup: '',
        suggestion: undefined,
        currentFiles: immediateFileData, // Start with empty array
      };

      console.log('📝 Creating turn immediately');
      setTurns((prev) => [...prev, newTurn]);
      setTypingTurnId(clientTurnId);

      // Reset scroll tracking
      userScrolledAwayRef.current = false;
      userScrolledAwayRightRef.current = false;
      isNearBottomRef.current = true;
      setTimeout(scrollMessagesToBottom, 0);

      // Upload files in background and update turn
      // Upload files and get metadata
      let uploadedFilesMetadata = [];
      if (files && files.length > 0) {
        console.log(`📎 Uploading ${files.length} files for turn ${currentTurnNumber}...`);

        // Upload each file and collect metadata
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          try {
            console.log(`📤 Uploading file: ${file.name}`);
            const fileMetadata = await handleFileUpload(file, currentTurnNumber, currentSessionId);

            if (fileMetadata) {
              uploadedFilesMetadata.push(fileMetadata);
              console.log(`✅ File uploaded: ${file.name}`, fileMetadata);

              // ✅ Update the specific file with server metadata
              setTurns((prev) => prev.map(t => {
                if (t.id === clientTurnId) {
                  const updatedFiles = [...t.currentFiles];
                  updatedFiles[i] = {
                    ...updatedFiles[i],
                    ...fileMetadata,
                    uploading: false, // ← Upload complete
                  };
                  return { ...t, currentFiles: updatedFiles };
                }
                return t;
              }));
            }
          } catch (error) {
            console.error('Error uploading file:', error);
            // Continue with other files even if one fails
            // Mark file as failed
            setTurns((prev) => prev.map(t => {
              if (t.id === clientTurnId) {
                const updatedFiles = [...t.currentFiles];
                updatedFiles[i] = {
                  ...updatedFiles[i],
                  uploading: false,
                  uploadError: true, // ← Show error state in UI
                };
                return { ...t, currentFiles: updatedFiles };
              }
              return t;
            }));
          }
        }
        console.log(`✅ All files uploaded. Metadata count: ${uploadedFilesMetadata.length}`);
        // Add to accumulated files list
        setAllFiles(prev => [...prev, ...files]);
        setAllFilesMetadata(prev => [...prev, ...uploadedFilesMetadata]); // ← Metadata for Firestore
      }

      // const newTurn = {
      //   id: clientTurnId,
      //   userText: text || '',
      //   botFollowup: '',
      //   suggestion: undefined,
      //   currentFiles: uploadedFilesMetadata,
      // };

      // console.log('📝 Creating turn with files:', uploadedFilesMetadata);
      // setTurns((prev) => [...prev, newTurn]);
      // setTypingTurnId(clientTurnId);

      // // Reset scroll tracking
      // userScrolledAwayRef.current = false;
      // userScrolledAwayRightRef.current = false;
      // isNearBottomRef.current = true;
      // setTimeout(scrollMessagesToBottom, 0);

      let passedGuardrail = false;

      const guardrailResponse = await checkGuardrail(text, files);
      passedGuardrail = guardrailResponse.status === 'success' && guardrailResponse.message === '1';

      if (passedGuardrail) {
        // ✅ PASSED GUARDRAIL
        const updatedValidMessages = text && text.trim()
          ? [...validUserMessages, text]
          : validUserMessages;
        setValidUserMessages(updatedValidMessages);
        console.log('✅ all files check', allFiles.length, allFiles);
        const allFilesForConversion = [...allFiles, ...(files || [])];
        const fileMsgs = allFilesForConversion.length > 0
          ? await convertFilesToBase64(allFilesForConversion)
          : [];

        // Show suggestion button immediately
        setTurns((prev) =>
          prev.map((t) => (t.id === clientTurnId ? { ...t, suggestion: '' } : t))
        );

        // Auto-open right pane and set up typing indicators
        setSuggestions((prev) => ({ ...prev, [clientTurnId]: '' }));
        setActiveSuggestionTurnId(clientTurnId);

        let clarificationComplete = false;
        let optimizationComplete = false;
        let optimizationText = '';
        let assistantMessageContent = '';
        let extractedOptimizationsData = {};

        // Stream clarifications to left pane
        const clarificationsPromise = (async () => {
          try {
            let streamedFollowup = '';
            await streamClarifications(updatedValidMessages, fileMsgs, (chunk) => {
              streamedFollowup += chunk;
              assistantMessageContent = streamedFollowup;
              setTurns((prev) =>
                prev.map((t) => (t.id === clientTurnId ? { ...t, botFollowup: streamedFollowup } : t))
              );
            });
            clarificationComplete = true;
          } catch (error) {
            console.error('Error streaming clarifications:', error);
            setTurns((prev) =>
              prev.map((t) => (t.id === clientTurnId ? {
                ...t,
                botFollowup: t.botFollowup + '\n\n[Error loading clarifications]'
              } : t))
            );
          } finally {
            if (clarificationComplete) {
              setTypingTurnId(null);
              userScrolledAwayRef.current = false;
            }
          }
        })();

        // Stream optimizations to right pane
        const optimizationsPromise = (async () => {
          try {
            // ✅ START LOADING ANIMATION - wraps the API call delay
            setIsLoadingSuggestions(true);
            setTypingSuggestionId(clientTurnId);

            let streamedSuggestion = '';
            let firstChunkReceived = false;

            await streamOptimizations(updatedValidMessages, fileMsgs, (chunk) => {
              // ✅ STOP LOADING on first chunk - streaming has begun
              if (!firstChunkReceived) {
                setIsLoadingSuggestions(false);
                firstChunkReceived = true;
              }

              streamedSuggestion += chunk;
              optimizationText = streamedSuggestion;
              setSuggestions((prev) => ({
                ...prev,
                [clientTurnId]: streamedSuggestion,
              }));
            });

            optimizationComplete = true;

            // After optimization streaming completes, extract suggestions
            try {
              console.log('🔍 Extracting optimizations...');
              const extractResult = await extractOptimizations(optimizationText);
              if (extractResult.status === 'success' && extractResult.message) {

                extractedOptimizationsData = extractResult.message;
                setExtractedSuggestions(prev => ({
                  ...prev,
                  [clientTurnId]: extractResult.message
                }));
                console.log('✅ Optimizations extracted:', extractResult.message);
              } else {
                // No suggestions found
                extractedOptimizationsData = {};
                setExtractedSuggestions(prev => ({
                  ...prev,
                  [clientTurnId]: {}
                }));
                console.log('ℹ️ No optimization suggestions found');
              }
            } catch (error) {
              console.error('Error extracting optimizations:', error);
              extractedOptimizationsData = {};
              setExtractedSuggestions(prev => ({
                ...prev,
                [clientTurnId]: {}
              }));
            }
          } catch (error) {
            console.error('Error streaming optimizations:', error);
            setSuggestions((prev) => ({
              ...prev,
              [clientTurnId]: (prev[clientTurnId] || '') + '\n\n[Error loading optimization suggestions]',
            }));
          } finally {
            if (optimizationComplete) {
              setTypingSuggestionId(null);
              userScrolledAwayRightRef.current = false;
            }
            // Always stop loading animation
            setIsLoadingSuggestions(false);
          }
        })();

        await Promise.all([clarificationsPromise, optimizationsPromise]);

        // ✅ Save turn to Firestore after streaming completes
        if (currentSessionId) {
          try {
            const turnData = {
              turnNumber: currentTurnNumber,
              userMessage: {
                id: clientTurnId,
                content: text || '',
                timestamp: new Date().toISOString(),
                files: uploadedFilesMetadata
              },
              assistantMessage: {
                id: generateId(),
                content: assistantMessageContent,
                timestamp: new Date().toISOString(),
                isStreaming: false
              },
              suggestions: {
                rawContent: optimizationText,
                extracted: extractedOptimizationsData,
              }
            };

            console.log('💾 Saving turn with extracted suggestions:', extractedOptimizationsData, extractedSuggestions[clientTurnId]);

            const result = await apiService.saveTurn(currentSessionId, turnData);

            // Map client turn ID to Firestore turn ID
            if (result.turnId) {
              turnMappingsRef.current[clientTurnId] = result.turnId;
              console.log('✅ Turn saved to Firestore:', result.turnId);
            }
          } catch (error) {
            console.error('Error saving turn to Firestore:', error);
          }
        }

      } else {
        // ❌ FAILED GUARDRAIL
        const errorMessage =
          'I apologize, but your query doesn\'t appear to be related to construction, structural engineering, or optimization. Please ask a question within these domains.';

        let streamedFollowup = '';
        await streamText(errorMessage, (ch) => {
          streamedFollowup += ch;
          setTurns((prev) =>
            prev.map((t) => (t.id === clientTurnId ? { ...t, botFollowup: streamedFollowup } : t))
          );
        });
        setTypingTurnId(null);
        userScrolledAwayRef.current = false;
      }
    }
    catch (error) {
      setIsLoadingSuggestions(false); // Ensure loading stops on error
      console.error('Error in handleSend:', error);
      const errorMessage = 'Sorry, I encountered an error processing your request. Please try again.';
      let streamedFollowup = '';
      await streamText(errorMessage, (ch) => {
        streamedFollowup += ch;
        setTurns((prev) =>
          prev.map((t) => (t.id === clientTurnId ? { ...t, botFollowup: streamedFollowup } : t))
        );
      });
      setTypingTurnId(null);
      userScrolledAwayRef.current = false;
    } finally {
      // Always clear loading at the end
      setIsSending(false);
      console.log('handleSend completed for turn:');
    }
  };

  // Handle suggestion selection confirmation
  const confirmSuggestionSelection = (turnId, checkedKeys) => {
    setSelectedSuggestions(prev => ({
      ...prev,
      [turnId]: checkedKeys
    }));
  };
  
  // Generate report handler with API + Loading states
  const handleGenerateReport = async (turnId, overrideSelections = null) => {
    const checkedSuggestions = overrideSelections !== null
      ? overrideSelections
      : (selectedSuggestions[turnId] || []);
    // Allow generating report even with 0 selections (as per hardcoded version)

    const optimizationText = suggestions[turnId] || '';

    // Determine report version
    const existingReports = reports[turnId] || [];
    const newVersion = existingReports.length + 1;

    // Switch to show this report in right pane
    setActiveReportId({ turnId, version: newVersion });
    userScrolledAwayRightRef.current = false;

    // 🔧 Initialize report WITH the selected suggestions
    setReports(prev => ({
      ...prev,
      [turnId]: [
        ...(prev[turnId] || []),
        {
          version: newVersion,
          content: '',
          measurements: '',
          isComplete: false,
          selectedSuggestions: checkedSuggestions // ← Add this
        }
      ]
    }));

    try {
      // ✅ Set typing indicator for streaming
      setTypingReportId({ turnId, version: newVersion });

      // ✅ START LOADING ANIMATION - wraps the API call delays
      setIsLoadingReport(true);
      setReportLoadingPhase('measurements');  // Phase 1
      // Step 1: Get measurements (loading shows during this API call)
      const fileMsgs = allFiles.length > 0 ? await convertFilesToBase64(allFiles) : [];
      const measurementsResult = await getMeasurements(validUserMessages, fileMsgs);
      const measurementsText = measurementsResult.message || '';

      // Update measurements in report
      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r =>
          r.version === newVersion
            ? { ...r, measurements: measurementsText }
            : r
        )
      }));

      setReportLoadingPhase('costs');  // Phase 2
      // material cost list

      const costDetailsResult = await getMaterialCostList(validUserMessages, optimizationText, checkedSuggestions, measurementsText);
      const costDetailsText = costDetailsResult.message || '';

      // Step 2: Stream report (loading continues until first chunk)
      setReportLoadingPhase('streaming');  // Phase 3
      let streamedReport = '';
      let firstChunkReceived = false;

      await streamReport(
        validUserMessages,
        optimizationText,
        checkedSuggestions,
        measurementsText,
        costDetailsText,
        (chunk) => {
          // ✅ STOP LOADING on first chunk - streaming has begun
          if (!firstChunkReceived) {
            setIsLoadingReport(false);
            firstChunkReceived = true;
          }

          streamedReport += chunk;
          setReports(prev => ({
            ...prev,
            [turnId]: prev[turnId].map(r =>
              r.version === newVersion
                ? { ...r, content: streamedReport }
                : r
            )
          }));
        }
      );

      // ✅ AFTER STREAMING COMPLETES: Auto-generate and cache PDF
      console.log('📄 Report streaming complete. Generating and caching PDF...');

      // ✅ Save report to Firestore
      if (sessionId) {
        try {
          const firestoreTurnId = turnMappingsRef.current[turnId];
          if (firestoreTurnId) {
            await apiService.saveReport(sessionId, firestoreTurnId, {
              version: newVersion,
              content: streamedReport,
              selectedSuggestions: checkedSuggestions
            });
            console.log('✅ Report saved to Firestore');
          }
        } catch (error) {
          console.error('Error saving report to Firestore:', error);
        }
      }

      // Generate PDF via API
      const response = await fetch(`${WEB_SERVER_API_BASE_URL}/pdf/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          markdown: streamedReport, // Use the actual streamed content
          title: 'Alpherra Report',
          version: newVersion
        })
      });

      if (response.ok) {
        const pdfBlob = await response.blob();

        // ✅ Save to IndexedDB cache
        await savePDFToCache(turnId, newVersion, pdfBlob);
        console.log('✅ PDF cached successfully');
      } else {
        console.warn('⚠️ Could not cache PDF');
      }


      // Mark report as complete
      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r =>
          r.version === newVersion
            ? { ...r, isComplete: true }
            : r
        )
      }));

    } catch (error) {
      console.error('Error generating report:', error);
      setReports(prev => ({
        ...prev,
        [turnId]: prev[turnId].map(r =>
          r.version === newVersion
            ? { ...r, content: r.content + '\n\n[Error generating report]', isComplete: true }
            : r
        )
      }));
    } finally {
      setTypingReportId(null);
      userScrolledAwayRightRef.current = false;
      // Always stop loading animation
      setIsLoadingReport(false);
      setReportLoadingPhase('init');  // Reset
    }
  };

  // Toggle suggestion pane (shows optimization suggestions)
  const toggleSuggestion = (id) => {
    if (activeSuggestionTurnId === id && !activeReportId) {
      setActiveSuggestionTurnId(null);
    } else {
      setActiveSuggestionTurnId(id);
      setActiveReportId(null); // Close any report view
    }
  };

  // Toggle report view
  const toggleReport = (turnId, version) => {
    const reportKey = { turnId, version };
    if (activeReportId?.turnId === turnId && activeReportId?.version === version) {
      setActiveReportId(null);
      setActiveSuggestionTurnId(null);
    } else {
      setActiveReportId(reportKey);
      setActiveSuggestionTurnId(turnId); // Keep turn active
    }
  };

  // Computed values
  const activeTurn = activeSuggestionTurnId
    ? turns.find((t) => t.id === activeSuggestionTurnId) || null
    : null;
  const isRightPaneOpen = !!activeTurn;

  // Get current right pane content type
  const getRightPaneContent = () => {
    if (!activeSuggestionTurnId) return null;

    if (activeReportId && activeReportId.turnId === activeSuggestionTurnId) {
      // Show report
      const turnReports = reports[activeReportId.turnId] || [];
      const report = turnReports.find(r => r.version === activeReportId.version);
      return {
        type: 'report',
        content: report?.content || '',
        isComplete: report?.isComplete || false,
        version: activeReportId.version,
        selectedSuggestions: report?.selectedSuggestions || []
      };
    } else {
      // Show optimization suggestions
      return {
        type: 'suggestions',
        content: suggestions[activeSuggestionTurnId] || '',
        extracted: extractedSuggestions[activeSuggestionTurnId] || {},
        selected: selectedSuggestions[activeSuggestionTurnId] || []
      };
    }
  };

  const handleRightPaneFeedback = (contentId, feedbackType, comment, metadata) => {
    console.log('Right pane feedback:', {
      contentId,
      feedbackType,
      comment,
      metadata
    });

    // Route to appropriate feedback handler based on content type
    if (metadata.contentType === 'suggestions') {
      // Save suggestions feedback
      saveSuggestionsFeedbackManually(metadata.turnId, feedbackType, comment);

    } else if (metadata.contentType === 'report') {
      // Save report feedback
      saveReportFeedbackManually(metadata.turnId, metadata.version, feedbackType, comment);

    }
  };

  // Save suggestions feedback handler
  const saveSuggestionsFeedbackManually = async (turnId, feedbackType, comment = '') => {
    if (!sessionId) return;

    const firestoreTurnId = turnMappingsRef.current[turnId];
    if (!firestoreTurnId) {
      console.warn('No Firestore turn ID found for client turn:', turnId);
      return;
    }

    try {
      if (feedbackType === null) {
        // Remove feedback
        await apiService.removeSuggestionsFeedback(sessionId, firestoreTurnId);
        console.log('✅ Suggestions feedback removed');
      } else {
        // Save or update feedback
        await apiService.saveSuggestionsFeedback(sessionId, firestoreTurnId, {
          type: feedbackType,
          comment
        });
        console.log('✅ Suggestions feedback saved');
      }
    } catch (error) {
      console.error('Error saving suggestions feedback:', error);
    }
  };

  // Save report feedback handler
  const saveReportFeedbackManually = async (turnId, reportVersion, feedbackType, comment = '') => {
    if (!sessionId) return;

    const firestoreTurnId = turnMappingsRef.current[turnId];
    if (!firestoreTurnId) {
      console.warn('No Firestore turn ID found for client turn:', turnId);
      return;
    }

    const reportId = `report_v${reportVersion}`;

    try {
      if (feedbackType === null) {
        // Remove feedback
        await apiService.removeReportFeedback(sessionId, firestoreTurnId, reportId);
        console.log('✅ Report feedback removed');
      } else {
        // Save or update feedback
        await apiService.saveReportFeedback(sessionId, firestoreTurnId, reportId, {
          type: feedbackType,
          comment
        });
        console.log('✅ Report feedback saved');
      }
    } catch (error) {
      console.error('Error saving report feedback:', error);
    }
  };

  return {
    // State
    chatStarted,
    turns,
    activeSuggestionTurnId,
    suggestions,
    typingTurnId,
    typingSuggestionId,
    activeTurn,
    isRightPaneOpen,

    extractedSuggestions,
    selectedSuggestions,
    reports,
    activeReportId,
    typingReportId,

    isSending,

    // Refs
    messagesContainerRef,
    rightPaneRef,

    // Handlers
    handleSend,
    toggleSuggestion,
    toggleReport,
    onScrollMessages,
    handleRightPaneScroll,
    closeSuggestionPane: () => {
      setActiveSuggestionTurnId(null);
      setActiveReportId(null);
    },
    confirmSuggestionSelection,
    handleGenerateReport,
    getRightPaneContent,
    handleRightPaneFeedback,

    isLoadingSuggestions,
    isLoadingReport,
    reportLoadingPhase, 
    setReportLoadingPhase,

    // exports, firestore change
    // firestoreSessionId,
    // handleFileUpload,
    handleUpdateChatName,
    saveFeedbackManually,
    saveSuggestionsFeedbackManually,
    saveReportFeedbackManually,
    sessionId,
    chatName,
  };
}
